/**
 * Mycelium Transport Registry - get/set the active transport
 *
 * PGMQ (PgmqMyceliumTransport) is the only transport. The multi-transport
 * fan-out machinery and routing config were removed 2026-10-11: the qstash,
 * redis-stream and pubsub transports were deleted and production always
 * constructed PGMQ directly.
 *
 * @module mycelium-transport-registry
 * @since 2026-02-22
 * @updated 2026-10-11 Single-transport registry; multi-transport removed
 */

import type { IMyceliumTransport, MyceliumEventPayload } from '@flowstate/mycelium-core'
import { setMyceliumLogReporter, type MyceliumLogReport } from '../lib/logger'

/** Supported transport type identifier */
export type MyceliumTransportType = 'pgmq'

let activeTransport: IMyceliumTransport | null = null
let activeTransportType: MyceliumTransportType = 'pgmq'

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

function extractMyceliumMeta(context?: Record<string, unknown>): Record<string, unknown> | undefined {
  const candidate = context?.mycelium
  return candidate && typeof candidate === 'object'
    ? candidate as Record<string, unknown>
    : undefined
}

function omitMyceliumControl(context?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!context) {
    return undefined
  }

  const { mycelium: _mycelium, ...rest } = context
  return Object.keys(rest).length > 0 ? rest : undefined
}

function buildMyceliumLogEvent(report: MyceliumLogReport): MyceliumEventPayload {
  const context = report.context as Record<string, unknown> | undefined
  const myceliumMeta = extractMyceliumMeta(context)
  const priority = report.level === 'critical' ? 'high' : 'normal'

  return {
    domain: 'system',
    type: report.level === 'warn' ? 'system_warning_reported' : 'system_error_reported',
    userId: asString(myceliumMeta?.userId) ?? asString(context?.userId) ?? asString(context?.user_id),
    orgId: asString(myceliumMeta?.orgId) ?? asString(context?.orgId) ?? asString(context?.org_id),
    teamId: asString(myceliumMeta?.teamId) ?? asString(context?.teamId) ?? asString(context?.team_id),
    payload: {
      source: asString(myceliumMeta?.source) ?? report.namespace ?? 'logger:root',
      level: report.level,
      message: report.message,
      namespace: report.namespace,
      reportedAt: report.timestamp,
      priority,
      context: omitMyceliumControl(context),
      tags: Array.isArray(myceliumMeta?.tags)
        ? myceliumMeta.tags.filter((tag): tag is string => typeof tag === 'string')
        : undefined,
    },
  }
}

function syncLoggerReporting(transport: IMyceliumTransport): void {
  setMyceliumLogReporter(async (report) => {
    await transport.publish(buildMyceliumLogEvent(report))
  })
}

/**
 * Get the active Mycelium transport. Use this instead of myceliumEventBackbone.publish() at call sites.
 */
export function getMyceliumTransport(): IMyceliumTransport {
  if (!activeTransport) {
    throw new Error('Mycelium transport not initialized. Call setMyceliumTransport() first.')
  }
  return activeTransport
}

/**
 * Get the active transport type identifier.
 */
export function getMyceliumTransportType(): MyceliumTransportType {
  return activeTransportType
}

/**
 * Set the active transport. Call once at startup.
 *
 * @param transport - Transport instance
 * @param type - Transport type identifier
 */
export function setMyceliumTransport(
  transport: IMyceliumTransport,
  type?: MyceliumTransportType
): void {
  activeTransport = transport
  if (type) {
    activeTransportType = type
  }
  syncLoggerReporting(activeTransport)
}
